import { useTranslation } from "react-i18next";
import { Check, Lock, Shield } from "lucide-react";
import { Logo } from "@/components/ui";
import { BrandLockup, BrandShell } from "./AuthShell";

// PlatformSetupLockup is the "Klutch Platform · CONSOLE" wordmark (purple mark +
// console chip) shown on the platform first-time-setup panels (set/reset password).
export function PlatformSetupLockup() {
  const { t } = useTranslation();
  return (
    <BrandLockup
      accent="var(--color-platform)"
      label={t("platformPassword.wordmark")}
      chip={
        <span className="inline-flex items-center gap-1.5 rounded-full border border-platform-border bg-platform-bg px-2.5 py-1 font-mono text-[11px] tracking-wide text-platform-ink">
          <Shield size={12} /> {t("platformPassword.consoleTag")}
        </span>
      }
    />
  );
}

const FEATURES = ["adminLogin.feature1", "adminLogin.feature2", "adminLogin.feature3"];

const STATS = [
  { value: "adminLogin.stat1Value", label: "adminLogin.stat1Label" },
  { value: "adminLogin.stat2Value", label: "adminLogin.stat2Label" },
  { value: "adminLogin.stat3Value", label: "adminLogin.stat3Label" },
];

// PlatformBrand is the super-admin / control-plane panel: the same layout as the
// tenant LoginBrand but on the teal platform axis (6.6), with a PLATFORM-tagged
// lockup, a teal-winged mark, and cross-tenant operator messaging + stats.
export function PlatformBrand() {
  const { t } = useTranslation();
  return (
    <BrandShell glow="teal">
      <div className="flex items-center gap-3">
        <Logo size={30} withWordmark={false} accent="var(--color-teal)" />
        <div className="flex items-center gap-2.5">
          <span className="font-head text-[22px] font-bold text-ink">Klutch</span>
          <span className="rounded-md bg-teal-bg px-2 py-1 font-mono text-[11px] tracking-wide text-teal-ink">
            {t("adminLogin.platformTag")}
          </span>
        </div>
      </div>

      <div className="flex max-w-xl flex-col gap-5">
        <span className="font-mono text-xs tracking-[0.14em] text-teal-ink">
          {t("adminLogin.eyebrow")}
        </span>
        <h1 className="max-w-130 font-head text-[44px] font-bold leading-[1.1] tracking-tight text-ink">
          {t("adminLogin.headline")}
        </h1>
        <p className="max-w-117.5 text-[16px] leading-relaxed text-muted">{t("adminLogin.tagline")}</p>

        <ul className="flex flex-col gap-3 pt-2">
          {FEATURES.map((key) => (
            <li key={key} className="flex items-center gap-3 text-[15px] text-ink-2">
              <span className="grid size-5.5 shrink-0 place-items-center rounded-full bg-teal-bg text-teal-ink">
                <Check size={14} strokeWidth={2.5} />
              </span>
              {t(key)}
            </li>
          ))}
        </ul>

        <div className="flex flex-col gap-4 pt-4">
          <div className="h-px w-full bg-edge-2" />
          <div className="flex gap-7">
            {STATS.map(({ value, label }) => (
              <div key={value} className="flex flex-col gap-1">
                <div className="font-head text-[26px] font-bold text-ink">{t(value)}</div>
                <div className="text-[13px] text-muted">{t(label)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2.5 text-[13px] text-muted-2">
        <Lock size={16} />
        {t("adminLogin.brandFooter")}
      </div>
    </BrandShell>
  );
}
